import { countries, type CountryIso2 } from '~/constants/countries';
import { type BaseAddress, type JSONBaseAddress } from '~/types/Address';

export interface JSONAPIAddress extends JSONBaseAddress {
  id?: number;
  is_default_return?: boolean;
  is_default_sender?: boolean;
  object_created?: Date;
  object_id?: string;
  object_updated?: Date;
  store_address_id?: Maybe<string>;
  store_platform_names?: Maybe<string[]>;
}

export interface SavedAPIAddress extends BaseAddress {
  addressId?: string;
  id: number;
  isDefaultReturn?: boolean;
  isDefaultSender?: boolean;
  objectCreated?: Date;
  objectId?: string;
  objectOwnerID?: number;
  objectUpdated?: Date;
  storeAddressId?: Maybe<string>;
  storePlatformNames?: Maybe<string[]>;
}

export type APIAddress = OptionalId<SavedAPIAddress>;

export function fromJSON(jsonAddress: JSONAPIAddress): SavedAPIAddress;
export function fromJSON(jsonAPIAddress: null | undefined): null;
export function fromJSON(jsonAddress: JSONAPIAddress | null | undefined): SavedAPIAddress | null;
export function fromJSON(
  jsonAPIAddress: JSONAPIAddress | null | undefined,
): SavedAPIAddress | null {
  if (!jsonAPIAddress) return null;

  return {
    city: jsonAPIAddress.city,
    company: jsonAPIAddress.company,
    country: countries[jsonAPIAddress.country as CountryIso2],
    email: jsonAPIAddress.email,
    id: jsonAPIAddress.id!, // Assuming data is from server, so address was saved
    isDefaultReturn: jsonAPIAddress.is_default_return ? jsonAPIAddress.is_default_return : false,
    isDefaultSender: jsonAPIAddress.is_default_sender ? jsonAPIAddress.is_default_sender : false,
    name: jsonAPIAddress.name,
    objectCreated: jsonAPIAddress.object_created
      ? new Date(jsonAPIAddress.object_created)
      : undefined,
    objectId: jsonAPIAddress.object_id,
    objectUpdated: jsonAPIAddress.object_updated
      ? new Date(jsonAPIAddress.object_updated)
      : undefined,
    phone: jsonAPIAddress.phone,
    state: jsonAPIAddress.state,
    storeAddressId: jsonAPIAddress.store_address_id ? jsonAPIAddress.store_address_id : null,
    storePlatformNames: jsonAPIAddress.store_platform_names
      ? jsonAPIAddress.store_platform_names
      : null,
    street1: jsonAPIAddress.street1,
    street2: jsonAPIAddress.street2,
    streetNumber: jsonAPIAddress.street_no,
    zip: jsonAPIAddress.zip,
  };
}

export const toJSON = (address: APIAddress): Maybe<JSONAPIAddress> => {
  if (!address) return null;

  return {
    city: address.city,
    company: address.company,
    country: address.country ? address.country.iso2 : '',
    email: address.email,
    id: address.id,
    is_default_return: address.isDefaultReturn ? address.isDefaultReturn : false,
    is_default_sender: address.isDefaultSender ? address.isDefaultSender : false,
    name: address.name,
    phone: address.phone ? address.phone : '',
    state: address.state,
    street_no: address.streetNumber,
    street1: address.street1,
    street2: address.street2,
    zip: address.zip,
  };
};

export const newAPIAddress = (address: BaseAddress): APIAddress => {
  const { city, company, country, email, name, phone, state, street1, street2, streetNumber, zip } =
    address;

  const newAPIAddress: APIAddress = {
    city: city || '',
    company: company || '',
    country: country || null,
    email: email || '',
    name: name || '',
    phone: phone || '',
    state: state || '',
    street1: street1 || '',
    street2: street2 || '',
    streetNumber: streetNumber || '',
    zip: zip || '',
  };

  return newAPIAddress;
};

export const APIAddressToBaseAddress = (address?: Maybe<APIAddress>): BaseAddress => ({
  city: address?.city || '',
  company: address?.company || '',
  country: address?.country || null,
  email: address?.email || '',
  name: address?.name || '',
  phone: address?.phone || '',
  state: address?.state || '',
  street1: address?.street1 || '',
  street2: address?.street2 || '',
  streetNumber: address?.streetNumber || '',
  zip: address?.zip || '',
});

export default APIAddress;
